/**
 * Drawer z prawej z oficjalnym PDF-em faktury KSeF (generator MF, `lib/ksef-pdf.ts`).
 *
 * Jeden komponent dla obu miejsc: lista „Dokumenty KSeF" (dokłada swoje akcje w `children` —
 * status, utworzenie rekordu, XML) i zakładka „Załączniki" faktury zakupu/sprzedaży
 * (sam podgląd). PDF powstaje w locie z XML-a — nic nie jest zapisywane w magazynie plików.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Download, ExternalLink } from 'lucide-react';

import { Button } from '@/components/Button';
import { Drawer } from '@/components/Drawer';
import { Spinner } from '@/components/Spinner';
import { generateKsefInvoicePdf, type KsefPdfInput } from '@/lib/ksef-pdf';
import { useTenantTimeZone } from '@/lib/settings/time-zone-api';

export interface KsefPdfDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Dane do PDF; `null`, gdy ich nie ma — powód mówi `sourceState`. */
  source: KsefPdfInput | null;
  /**
   * Stan danych, gdy `source` jest `null`: `loading` = jeszcze się wczytują (spinner),
   * `noXml` = faktura bez zapisanego XML, `error` = nie udało się jej wczytać. Domyślnie `loading`.
   */
  sourceState?: KsefPdfSourceState;
  /** Nazwa pliku przy pobraniu (bez rozszerzenia) — domyślnie numer KSeF. */
  fileBaseName?: string;
  title?: ReactNode;
  /** Treść nad podglądem (np. status i akcje na liście „Dokumenty KSeF"). */
  header?: ReactNode;
  /** Treść pod podglądem. */
  children?: ReactNode;
}

export type KsefPdfSourceState = 'loading' | 'noXml' | 'error';

type PdfState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'error' };

export function KsefPdfDrawer({
  open,
  onClose,
  source,
  sourceState = 'loading',
  fileBaseName,
  title,
  header,
  children,
}: KsefPdfDrawerProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<PdfState>({ kind: 'idle' });
  // Data przyjęcia w KSeF na PDF — w strefie tenanta.
  const tenantTimeZone = useTenantTimeZone();

  const xml = source?.xml;
  const ksefNumber = source?.ksefNumber;
  const verificationUrl = source?.verificationUrl;
  const acquisitionDate = source?.acquisitionDate;

  useEffect(() => {
    if (!open || !xml || !ksefNumber) {
      setState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ kind: 'loading' });
    generateKsefInvoicePdf({ xml, ksefNumber, verificationUrl, acquisitionDate }, tenantTimeZone)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ kind: 'ready', url: objectUrl });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[KsefPdfDrawer] generowanie PDF nie powiodło się', err);
        setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, xml, ksefNumber, verificationUrl, acquisitionDate, tenantTimeZone]);

  const downloadName = `${fileBaseName ?? ksefNumber ?? 'faktura-ksef'}.pdf`;

  const handleDownload = () => {
    if (state.kind !== 'ready') return;
    const link = document.createElement('a');
    link.href = state.url;
    link.download = downloadName;
    link.click();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="half"
      title={title ?? t('ksefPdf.drawerTitle')}
      description={ksefNumber}
    >
      <div className="flex flex-col gap-3" data-testid="ksef-pdf-drawer">
        {header}

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ExternalLink className="h-4 w-4" />}
            disabled={state.kind !== 'ready'}
            onClick={() => state.kind === 'ready' && window.open(state.url, '_blank', 'noopener')}
          >
            {t('ksefPdf.openInNewTab')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={state.kind !== 'ready'}
            onClick={handleDownload}
            data-testid="ksef-pdf-download"
          >
            {t('ksefPdf.download')}
          </Button>
        </div>

        <div className="h-[75vh] overflow-hidden rounded-lg border border-border-primary bg-bg-tertiary">
          {(state.kind === 'loading' || (!source && sourceState === 'loading')) && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-text-tertiary">
              <Spinner />
              {t('ksefPdf.generating')}
            </div>
          )}
          {!source && sourceState !== 'loading' && (
            <div
              className="flex h-full items-center justify-center gap-2 px-4 text-center text-sm text-text-tertiary"
              data-testid="ksef-pdf-unavailable"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {sourceState === 'noXml' ? t('ksefPdf.noXmlPreview') : t('ksefPdf.loadError')}
            </div>
          )}
          {state.kind === 'error' && (
            <div
              className="flex h-full items-center justify-center gap-2 px-4 text-center text-sm text-error-500"
              data-testid="ksef-pdf-error"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {t('ksefPdf.error')}
            </div>
          )}
          {state.kind === 'ready' && (
            <iframe
              src={state.url}
              title={t('ksefPdf.iframeTitle', { ksefNumber })}
              className="h-full w-full"
              data-testid="ksef-pdf-frame"
            />
          )}
        </div>

        {children}
      </div>
    </Drawer>
  );
}
